<script>
import BaseView from "@/views/BaseView.vue";
import ChatMixin from "@/views/chats/ChatMixin.vue";
import FileMixin from "@/mixins/file.mixin";
import { mapState } from "pinia";
import { useAppStore } from "@/store/app.store.ts";
import { MessageType } from "@/data/chat.js";

export default {
  name: "ChatMessage",
  mixins: [BaseView, ChatMixin, FileMixin],
  props: {
    message: Object,
    thread: Object,
  },
  emits: ["media", "openMenu"],
  data() {
    return {
      showMenu: false,
      menuX: 0,
      menuY: 0,
      selectedMessageId: null,
    };
  },
  computed: {
    ...mapState(useAppStore, ["dispatcher", "dispatchers"]),
    name() {
      if (this.message.dispatcher) {
        return this.message.dispatcher.name;
      }
      return this.thread.name;
    },
    MessageType() {
      return MessageType;
    },
    isSystemMessage() {
      return this.message.type.startsWith("system_");
    },
    isUserInfoMessage() {
      return this.message.type === MessageType.UserInfo;
    },
    getSystemMessageColor() {
      switch (this.message.type) {
        case "system_success":
          return "success";
        case "system_warning":
          return "warning";
        case "system_error":
          return "error";
        default:
          return "grey-darken-2";
      }
    },
    getSystemMessageIcon() {
      switch (this.message.type) {
        case "system_success":
          return "mdi-check-circle-outline";
        case "system_warning":
          return "mdi-alert-circle-outline";
        case "system_error":
          return "mdi-alert-circle-outline";
        default:
          return null;
      }
    },
    deletedByInfo() {
      return this.dispatchers.find((dispatcher) => dispatcher.id === this.message.deletedBy);
    },
  },
  methods: {
    adaptMessage(text) {
      // escape any html tags
      text = text.replaceAll(/&/g, "&amp;").replaceAll(/</g, "&lt;").replaceAll(/>/g, "&gt;");

      // linkify links
      text = text.replaceAll(/(https?:\/\/\S+)/g, '<a href="$1" target="_blank">$1</a>');

      // add new lines
      text = text.replaceAll("\n", "<br />");

      return text;
    },
    fileClick() {
      if (!this.message.deletedBy) {
        this.$emit("media", this.message.id);
      }
    },
    openChatMenu(event) {
      if (this.dispatcher.isSuperAdmin) {
        event.preventDefault();
        this.$emit("openMenu", event, this.message);
      }
    },
  },
};
</script>

<template>
  <div>
    <div :data-id="message.id" class="message-block">
      <div v-if="isSystemMessage" class="mb-10 alert-block">
        <div :class="'text-' + getSystemMessageColor" class="d-flex justify-center align-center">
          <v-icon v-if="getSystemMessageIcon" :icon="getSystemMessageIcon" class="mr-2"></v-icon>
          <div>
            {{ message.text }}
          </div>
          <div class="text-grey-darken-1 ml-3 white-space-nowrap">
            {{ showMessageTime(message.created) }}
          </div>
        </div>
      </div>

      <div v-else-if="isUserInfoMessage" class="d-flex justify-center">
        <div class="bg-grey-lighten-3 px-3 py-1 rounded d-flex align-center justify-center">
          <div class="text-grey-darken-2">{{ message.text }}</div>

          <div class="text-grey-darken-1 ml-3 white-space-nowrap">
            {{ showMessageTime(message.created) }}
          </div>
        </div>
      </div>

      <div v-else :class="{ user: message.dispatcherId }" class="message-block__inner">
        <div
          v-if="message.dispatcherId"
          class="message-block__user-name mb-1 text-grey-darken-3 font-weight-medium text-caption"
        >
          {{ name }}
        </div>
        <div
          v-if="message.type === MessageType.Text"
          :class="{
            'message-block__text--deleted': message.deletedBy,
          }"
          class="message-block__text pa-3 pl-4 mb-2 text-grey-darken-3"
          @contextmenu="openChatMenu"
        >
          <div class="d-inline-flex flex-wrap justify-end">
            <span class="message-block__text-content" v-html="adaptMessage(message.text)" />
            <span v-if="!message.deletedBy" class="message-block__time">
              {{ showMessageTime(message.created) }}
              <span v-if="message.dispatcherId">
                <v-icon v-if="message.read" class="ml-2" color="success" size="small">mdi-check-all</v-icon>
                <v-icon v-else class="ml-2" color="success" size="small">mdi-check</v-icon>
              </span>
            </span>
          </div>

          <div v-if="message.deletedBy && deletedByInfo" class="text-grey-darken-2 font-weight-500 text-right mt-2">
            Deleted by {{ deletedByInfo.name }}
          </div>
        </div>
        <div
          v-if="message.type === MessageType.Image"
          :class="{
            'message-block__text--deleted': message.deletedBy,
          }"
          class="message-block__text pa-3 pl-4 mb-2 text-grey-darken-1"
          @contextmenu="openChatMenu"
        >
          <v-row class="d-flex">
            <v-col v-if="!message.deletedBy" style="flex: 0 0 100px" @click="fileClick">
              <img
                :height="90"
                :src="message.previewUrl"
                :width="90"
                alt="Image preview"
                class="rounded d-block"
                style="cursor: pointer; object-fit: cover"
              />
            </v-col>

            <v-col v-else>
              <div class="h-100 d-flex align-center justify-center bg-grey-lighten-4">
                <v-icon color="grey" size="32">mdi-file-document-outline</v-icon>
              </div>
            </v-col>
            <v-col>
              <div class="message-block__filename" @click="fileClick">
                {{ $filters.ellipsis(message.filename, 40, "middle") }}
              </div>
              <div class="message-block__filesize mt-3 mb-3 text-grey-darken-2">
                {{ $filters.bytes(message.size) }}
              </div>

              <div v-if="message.deletedBy && deletedByInfo" class="text-grey-darken-2 font-weight-500 mt-2">
                Deleted by {{ deletedByInfo.name }}
              </div>

              <div v-else class="message-block__file-action">
                <v-btn
                  class="text-uppercase font-size-12 font-weight-600"
                  color="primary"
                  variant="outlined"
                  @click="downloadUrl(message.url)"
                >
                  Download
                </v-btn>
                <div class="message-block__time mt-5">
                  {{ showMessageTime(message.created) }}
                  <span v-if="message.dispatcherId">
                    <v-icon v-if="message.read" class="ml-2" color="success" size="small">mdi-check-all</v-icon>
                    <v-icon v-else class="ml-2" color="success" size="small">mdi-check</v-icon>
                  </span>
                </div>
              </div>
            </v-col>
          </v-row>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/style/color";

.message-block__text-content {
  overflow-wrap: anywhere;
}

.alert-block {
  :deep(.v-icon) {
    font-size: 20px !important;
    width: 20px !important;
    height: 20px !important;
  }

  .v-alert {
    border-radius: 12px;
  }
}

.circle {
  display: inline-block;
  width: 5px;
  height: 5px;
  border-radius: 50%;
}

.status-label {
  line-height: 14px !important;
}

.day {
  position: relative;

  &:after {
    position: absolute;
    top: 50%;
    left: 0;
    transform: translateY(-50%);
    display: block;
    content: "";
    width: 100%;
    height: 1px;
    border: 1px solid #dee2e6;
  }

  span {
    background: #f8f9fa;
    display: inline-block;
    position: relative;
    z-index: 1;
  }
}

.bordered {
  border: solid 1px #616161;
}

.message {
  &-block:not(:last-child) {
    margin-bottom: 20px;
  }

  &-block__info {
    font-size: 14px;
  }

  .text {
    word-break: break-word;
  }

  &-block__inner {
    max-width: 90%;
    width: fit-content;

    @media (max-width: 991px) {
      max-width: 95%;
    }

    &.user {
      margin: 0 0 0 auto;
      text-align: right;
      width: fit-content;

      .message-block__text {
        background: $primary-light;
        color: $grey-text;
        border-radius: 8px 8px 0 8px;
        text-align: left;
        width: fit-content;
      }

      .message-block__time {
        color: $grey;
      }
    }
  }

  &-block__text {
    background: $primary-light-2;
    font-size: 14px;
    line-height: 16px;
    border-radius: 8px 8px 8px 0;
    width: fit-content;

    .text-span {
      overflow-wrap: anywhere;
    }
  }

  &-block__text--deleted {
    background-color: $grey-light3 !important;
    color: $grey-lighter-contrast !important;
  }

  &-block__time {
    float: right;
    color: #757575;
    font-size: 12px;
    line-height: 18px;
    margin-left: 16px;
  }

  &-block__filename {
    font-size: 16px;
    font-weight: 500;
    cursor: pointer;
    text-align: left;
  }

  &-block__filesize {
    font-size: 11px;
    line-height: 12px;
    text-align: left;
  }

  &-block__file-action {
    text-align: left;
  }
}

:deep(.message-block__text-content) {
  a {
    color: $primary;
  }
}
</style>
